import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from "react";
import {
  confirmScheduleIngestionWithResult,
  getScheduleIngestionReview,
  updateStagedGames,
} from "../../../../services/schedule-ingestions";
import { resolveGameConflict } from "../../../../services/tournament-games";
import { getTeams } from "../../../../services/teams";
import type { TeamResponse } from "../../../../types/api";
import type {
  ConfirmScheduleIngestionResult,
  ExtractedGame,
  ScheduleIngestionReviewResponse,
  ScheduleMergeGameResponse,
  ScheduleMergeResultResponse,
  TeamMatch,
  StagedGameEdit,
} from "../../../../types/schedule-ingestion";
import { mergeSummary } from "./build-itinerary/importSummary";
import { ApiError } from "../../../../services/api";
import { getSafeErrorMessage } from "../../../../utils/errorHandler";
import {
  browserTimeZone,
  inclusiveDateToExclusiveEndIso,
  inclusiveDateToStartIso,
} from "../../../../utils/calendarTime";
import {
  buildTeamResolutions,
  gameTimeLabel,
  initialChoiceFor,
  US_TIME_ZONES,
  type TeamChoice,
} from "./scheduleReview";
import "./ScheduleIngestionReview.css";

const CONFIRM_FALLBACK =
  "We could not create the tournament. Please try again.";
const LOAD_FALLBACK =
  "We could not load the extracted schedule. Please try again.";

interface ScheduleIngestionReviewProps {
  ingestionId: string;
  /**
   * When set, confirm REBUILDS this existing tournament (replace its games) instead of creating a
   * new one. The server keeps the event's title/dates in this mode.
   */
  targetCalendarEventId?: string;
  /** Seed for the tournament title, e.g. the event editor's current title. */
  defaultTitle?: string;
  /** Optional default timezone (IANA). Blank sends the org default. */
  defaultTimeZone?: string;
  /**
   * Fired after a successful confirm so the host can refresh the calendar. Carries
   * the discriminated result: `created` (a new tournament, HTTP 201) or `merged` (a
   * re-import merged into an existing tournament, HTTP 200). On the merge path this
   * component renders its own "what changed" summary in place, so the host should
   * just refresh the calendar and leave the review mounted.
   */
  onConfirmed: (result: ConfirmScheduleIngestionResult) => void;
  /**
   * Fired when the ingestion was already confirmed (either on load or on a 409
   * during confirm). The host routes the user to the existing tournament.
   */
  onAlreadyConfirmed: (calendarEventId: string) => void;
}

type LoadState = "loading" | "ready" | "confirmed" | "error";

const todayUtcDate = () => new Date().toISOString().slice(0, 10);

/**
 * Parse a single extracted `game.date` (free-form LLM text) into a UTC
 * `YYYY-MM-DD` string, or null if it isn't a date. We anchor to UTC so a
 * west-of-UTC viewer never sees the seeded date shift back a day.
 */
function parseGameDateUtc(raw: string | undefined): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(
    Date.UTC(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
  )
    .toISOString()
    .slice(0, 10);
}

/**
 * The earliest and latest parseable game dates, used to seed the tournament's
 * inclusive start/end. Falls back to today when nothing parses.
 */
function gameDateRange(games: ExtractedGame[]): { start: string; end: string } {
  const dates = games
    .map((g) => parseGameDateUtc(g.date))
    .filter((d): d is string => d !== null)
    .sort();
  if (dates.length === 0) {
    const today = todayUtcDate();
    return { start: today, end: today };
  }
  return { start: dates[0], end: dates[dates.length - 1] };
}

/**
 * How long to wait after the last keystroke before writing staged edits.
 *
 * Matches BatchExtractionReview's flush interval, which is the closest analogue in the codebase --
 * a review table of extracted rows edited cell by cell.
 */
const SAVE_DEBOUNCE_MS = 500;

export function ScheduleIngestionReview({
  ingestionId,
  targetCalendarEventId,
  defaultTitle,
  defaultTimeZone,
  onConfirmed,
  onAlreadyConfirmed,
}: ScheduleIngestionReviewProps) {
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [loadError, setLoadError] = useState("");

  const [teams, setTeams] = useState<TeamResponse[]>([]);
  const [teamMatches, setTeamMatches] = useState<TeamMatch[]>([]);

  // Editable per-row game fields.
  const [games, setGames] = useState<ExtractedGame[]>([]);
  // One team choice per DISTINCT raw name (keyed by the server's rawName).
  const [choices, setChoices] = useState<Map<string, TeamChoice>>(new Map());

  // Tournament header fields. Dates are inclusive and shown to the user; the
  // exclusive end the API wants is derived at confirm time.
  const [title, setTitle] = useState(defaultTitle?.trim() ?? "");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [timeZone, setTimeZone] = useState(
    defaultTimeZone?.trim() || browserTimeZone()
  );

  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");

  // Set once a re-import MERGES into an existing tournament (rebuild path). When
  // present we render the "what changed" summary in place of the review table.
  const [mergeResult, setMergeResult] =
    useState<ScheduleMergeResultResponse | null>(null);

  // One controller for every request this component makes (initial load AND the
  // confirm-path recovery refetch) so unmounting aborts them all and no state
  // is set after unmount.
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    controllerRef.current = controller;
    const { signal } = controller;

    const load = async () => {
      setLoadState("loading");
      setLoadError("");
      try {
        // Teams are best-effort: a failure just leaves the dropdown with no
        // existing-team options (the user can still create or leave unmatched).
        const [review, teamList] = await Promise.all([
          getScheduleIngestionReview(ingestionId, signal),
          getTeams().catch(() => [] as TeamResponse[]),
        ]);
        if (signal.aborted) return;

        if (review.resultCalendarEventId) {
          setLoadState("confirmed");
          onAlreadyConfirmed(review.resultCalendarEventId);
          return;
        }

        seedFromReview(review, teamList);
        setLoadState("ready");
      } catch (err) {
        if (signal.aborted) return;
        setLoadError(getSafeErrorMessage(err, LOAD_FALLBACK));
        setLoadState("error");
      }
    };

    void load();
    return () => controller.abort();
    // seedFromReview / onAlreadyConfirmed are stable enough for a one-shot load;
    // re-running only on ingestionId is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingestionId]);

  const seedFromReview = (
    review: ScheduleIngestionReviewResponse,
    teamList: TeamResponse[]
  ) => {
    setTeams(teamList);
    setTeamMatches(review.teamMatches);
    setGames(review.games);

    const nextChoices = new Map<string, TeamChoice>();
    for (const match of review.teamMatches) {
      nextChoices.set(match.rawName, initialChoiceFor(match));
    }
    setChoices(nextChoices);

    const range = gameDateRange(review.games);
    setStartDate((prev) => prev || range.start);
    setEndDate((prev) => prev || range.end);
  };

  // The distinct raw names come straight from the server's teamMatches so the
  // dropdowns and the confirm payload share ONE authoritative name set. Team
  // names on games are not editable, so this stays in sync with the rows.
  const distinctNames = useMemo(
    () => teamMatches.map((m) => m.rawName),
    [teamMatches]
  );

  // Team options for the existing-team part of every dropdown, sorted by name.
  const teamOptions = useMemo(
    () =>
      teams
        .map((t) => ({ teamId: t.id, name: t.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [teams]
  );

  const matchByName = useMemo(() => {
    const map = new Map<string, TeamMatch>();
    for (const match of teamMatches) map.set(match.rawName, match);
    return map;
  }, [teamMatches]);

  const setChoiceForName = useCallback((rawName: string, choice: TeamChoice) => {
    setChoices((prev) => new Map(prev).set(rawName, choice));
  }, []);

  // Edits not yet written to the server, keyed by sortOrder. Accumulated rather
  // than sent per keystroke, then flushed on a debounce.
  const pendingEditsRef = useRef<Map<number, StagedGameEdit>>(new Map());
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveError, setSaveError] = useState("");

  // Writes the queued edits. Until this existed the review table's edits went
  // nowhere at all: they lived in React state that handleConfirm never read, and
  // the confirm request carries no games -- so every correction a coach made was
  // silently dropped and the tournament was built from the raw extraction.
  const flushEdits = useCallback(async () => {
    const queued = Array.from(pendingEditsRef.current.values());
    if (queued.length === 0) return;

    // Cleared BEFORE the await, so an edit made while this request is in flight
    // queues for the next flush instead of being wiped by this one's success.
    pendingEditsRef.current = new Map();

    try {
      const review = await updateStagedGames(
        ingestionId,
        queued,
        controllerRef.current?.signal
      );
      if (controllerRef.current?.signal.aborted) return;

      // The server owns needsReview and the geocoded field data, and an edited
      // team name changes the match suggestions -- so take its version rather
      // than assuming our optimistic one still matches.
      setGames(review.games);
      setTeamMatches(review.teamMatches);
      setSaveError("");
    } catch (err) {
      if (controllerRef.current?.signal.aborted) return;
      setSaveError(
        getSafeErrorMessage(err, "We couldn't save that change. Try again.")
      );
    }
  }, [ingestionId]);

  const updateGameField = useCallback(
    (
      sortOrder: number,
      field: "time" | "field" | "date" | "homeTeam" | "awayTeam",
      value: string
    ) => {
      setGames((prev) =>
        prev.map((g) =>
          g.sortOrder === sortOrder ? { ...g, [field]: value } : g
        )
      );

      const queued = pendingEditsRef.current.get(sortOrder) ?? { sortOrder };
      pendingEditsRef.current.set(sortOrder, { ...queued, [field]: value });

      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
      flushTimerRef.current = setTimeout(() => void flushEdits(), SAVE_DEBOUNCE_MS);
    },
    [flushEdits]
  );

  // A pending debounce is an unsaved edit. Confirming through it would build the
  // tournament from the server's older copy -- the exact bug this replaced, just
  // with a shorter window.
  const flushBeforeConfirm = useCallback(async () => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    await flushEdits();
  }, [flushEdits]);

  useEffect(
    () => () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    },
    []
  );

  const handleConfirm = async () => {
    await flushBeforeConfirm();

    const trimmedTitle = title.trim();
    if (trimmedTitle.length === 0) {
      setConfirmError("Give the tournament a title.");
      return;
    }
    if (trimmedTitle.length > 200) {
      setConfirmError("The title is too long. Keep it under 200 characters.");
      return;
    }
    if (!startDate || !endDate) {
      setConfirmError("Pick a start and end date.");
      return;
    }
    if (endDate < startDate) {
      setConfirmError("The end date cannot be before the start date.");
      return;
    }

    // All-day start at midnight UTC; exclusive end is the day after the
    // inclusive last day the user chose (see calendarTime for the contract).
    const startsAtUtc = inclusiveDateToStartIso(startDate);
    const endsAtUtc = inclusiveDateToExclusiveEndIso(endDate);

    const teamResolutions = buildTeamResolutions(distinctNames, choices);
    const signal = controllerRef.current?.signal;

    setConfirming(true);
    setConfirmError("");
    try {
      const result = await confirmScheduleIngestionWithResult(
        ingestionId,
        {
          // In rebuild mode this merges into the target tournament's games; the server keeps its
          // title/dates, so those fields are ignored server-side but harmless to send.
          ...(targetCalendarEventId ? { targetCalendarEventId } : {}),
          title: trimmedTitle,
          startsAtUtc,
          endsAtUtc,
          timeZone: timeZone.trim() || undefined,
          teamResolutions,
        },
        signal
      );
      if (signal?.aborted) return;
      if (result.kind === "merged") {
        // Rebuild path: show the "what changed" summary in place and let the owner
        // resolve conflicts. We do NOT notify the host yet, because the host refresh
        // closes this editor — we defer that to the summary's Done button so the
        // owner can review before we tear the panel down.
        setMergeResult(result.result);
      } else {
        // Create path: the host shows its own created summary and closes the modal.
        onConfirmed(result);
      }
    } catch (err) {
      if (signal?.aborted) return;
      if (err instanceof ApiError && err.statusCode === 409) {
        // Already confirmed (or wrong state): reload to pick up the existing
        // tournament id and route the user there.
        try {
          const review = await getScheduleIngestionReview(ingestionId, signal);
          if (signal?.aborted) return;
          if (review.resultCalendarEventId) {
            onAlreadyConfirmed(review.resultCalendarEventId);
            return;
          }
        } catch {
          if (signal?.aborted) return;
          /* fall through to the generic message below */
        }
        setConfirmError(
          "This schedule was already turned into a tournament. Close this and open it from the calendar."
        );
        return;
      }
      setConfirmError(getSafeErrorMessage(err, CONFIRM_FALLBACK));
    } finally {
      if (!signal?.aborted) setConfirming(false);
    }
  };

  if (loadState === "loading") {
    return (
      <div className="ingest-progress" data-testid="schedule-review-loading">
        <div className="ingest-spinner" />
        <p className="ingest-phase">Getting your games ready to review...</p>
      </div>
    );
  }

  if (loadState === "confirmed") {
    // The host is routing the user to the existing tournament; render nothing.
    return null;
  }

  if (loadState === "error") {
    return (
      <div className="ingest-error" data-testid="schedule-review-load-error">
        <div className="ingest-error__icon" aria-hidden="true">
          ⚠️
        </div>
        <p className="ingest-error__msg" role="alert">
          {loadError}
        </p>
      </div>
    );
  }

  // Rebuild path: the re-import merged into the existing tournament. Show what
  // changed instead of the review table (the games are already applied server-side).
  if (mergeResult) {
    return (
      <MergeSummaryPanel
        result={mergeResult}
        onResolved={(gameId) =>
          setMergeResult((prev) =>
            prev
              ? {
                  ...prev,
                  conflicts: prev.conflicts.filter((c) => c.gameId !== gameId),
                  conflictCount: Math.max(0, prev.conflictCount - 1),
                }
              : prev
          )
        }
        onDone={(result) => onConfirmed({ kind: "merged", result })}
        signalRef={controllerRef}
      />
    );
  }

  // In rebuild mode the event already exists (the server keeps its title, dates,
  // and location); this pass is just about re-matching teams and refreshing games.
  const isRebuild = Boolean(targetCalendarEventId);

  return (
    <div className="review" data-testid="schedule-review">
      <p className="ingest-result__count">
        Review the {games.length} {games.length === 1 ? "game" : "games"} below and
        set the teams
        {isRebuild
          ? ". Games already on the event are updated; new ones are added."
          : ", then create the event."}
      </p>

      <div className="review-header">
        <div className="review-field">
          <label htmlFor="review-title">Event title</label>
          <input
            id="review-title"
            data-testid="review-title-input"
            className="review-input"
            value={title}
            maxLength={200}
            placeholder="e.g. PBR Summer Classic"
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>
        <div className="review-header__dates">
          <div className="review-field">
            <label htmlFor="review-start-date">Starts</label>
            <input
              id="review-start-date"
              data-testid="review-start-date"
              type="date"
              className="review-input"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="review-field">
            <label htmlFor="review-end-date">Ends</label>
            <input
              id="review-end-date"
              data-testid="review-end-date"
              type="date"
              className="review-input"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="review-field">
            <label htmlFor="review-timezone">Timezone</label>
            <select
              id="review-timezone"
              data-testid="review-timezone-input"
              className="review-input"
              value={timeZone}
              onChange={(e) => setTimeZone(e.target.value)}
            >
              {/* Preserve a non-US zone (e.g. the browser default) so the select still reflects it. */}
              {timeZone && !US_TIME_ZONES.some((z) => z.id === timeZone) && (
                <option value={timeZone}>{timeZone}</option>
              )}
              {US_TIME_ZONES.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="ingest-table-wrap review-table-wrap">
        <table className="ingest-table review-table" data-testid="review-games-table">
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Time</th>
              <th scope="col">Field</th>
              <th scope="col">Home</th>
              <th scope="col">Away</th>
            </tr>
          </thead>
          <tbody>
            {games.map((game) => (
              <tr
                key={game.sortOrder}
                className={game.needsReview ? "ingest-row--flagged" : undefined}
              >
                <td>
                  {/*
                    Editable, unlike every other release of this table. Dates are the
                    highest-risk extracted field -- the pipeline also rewrites outlying
                    months and drops games outside a target day, both silently -- and a
                    read-only wrong date left re-running the whole extraction as the only
                    way to fix one row.
                  */}
                  <input
                    className="review-cell-input"
                    data-testid={`review-date-${game.sortOrder}`}
                    aria-label={`Date for game ${game.sortOrder + 1}`}
                    value={game.date}
                    placeholder="TBD"
                    onChange={(e) =>
                      updateGameField(game.sortOrder, "date", e.target.value)
                    }
                  />
                  {/*
                    The count is baked into the review payload from the server's
                    SUGGESTED team match, so it reflects the initially matched
                    teams, not any dropdown override the user makes on this row.
                    The label says "suggested teams" so it never claims to be
                    live once a side is re-matched.
                  */}
                  {game.starredPlayerCount > 0 && (
                    <span
                      className="review-starred"
                      data-testid={`review-starred-${game.sortOrder}`}
                      aria-label={`${game.starredPlayerCount} starred ${
                        game.starredPlayerCount === 1 ? "player" : "players"
                      } on the suggested teams for this game`}
                    >
                      <span aria-hidden="true">★</span>
                      {game.starredPlayerCount} starred
                    </span>
                  )}
                </td>
                <td>
                  <input
                    className="review-cell-input"
                    data-testid={`review-time-${game.sortOrder}`}
                    aria-label={`Time for game ${game.sortOrder + 1}`}
                    value={game.time}
                    placeholder="TBD"
                    onChange={(e) =>
                      updateGameField(game.sortOrder, "time", e.target.value)
                    }
                  />
                  <GameTimeZone game={game} />
                </td>
                <td>
                  <input
                    className="review-cell-input"
                    data-testid={`review-field-${game.sortOrder}`}
                    aria-label={`Field for game ${game.sortOrder + 1}`}
                    value={game.field}
                    placeholder="TBD"
                    onChange={(e) =>
                      updateGameField(game.sortOrder, "field", e.target.value)
                    }
                  />
                </td>
                <td>
                  {/*
                    The raw extracted name, editable. The dropdown below maps a name
                    to a team; this fixes the name itself when the extraction misread
                    it ("Wolvez"), which no amount of re-mapping can. An edited name
                    has no server suggestion yet, so it re-flows through the matcher
                    as unmatched and the dropdown offers the full list.
                  */}
                  <input
                    className="review-cell-input"
                    data-testid={`review-home-name-${game.sortOrder}`}
                    aria-label={`Home team name for game ${game.sortOrder + 1}`}
                    value={game.homeTeam}
                    placeholder="TBD"
                    onChange={(e) =>
                      updateGameField(game.sortOrder, "homeTeam", e.target.value)
                    }
                  />
                  <TeamMatchSelect
                    rawName={game.homeTeam}
                    side="home"
                    sortOrder={game.sortOrder}
                    match={matchByName.get(game.homeTeam)}
                    teamOptions={teamOptions}
                    choice={choices.get(game.homeTeam)}
                    onChange={setChoiceForName}
                  />
                </td>
                <td>
                  {/*
                    The raw extracted name, editable. The dropdown below maps a name
                    to a team; this fixes the name itself when the extraction misread
                    it ("Wolvez"), which no amount of re-mapping can. An edited name
                    has no server suggestion yet, so it re-flows through the matcher
                    as unmatched and the dropdown offers the full list.
                  */}
                  <input
                    className="review-cell-input"
                    data-testid={`review-away-name-${game.sortOrder}`}
                    aria-label={`Away team name for game ${game.sortOrder + 1}`}
                    value={game.awayTeam}
                    placeholder="TBD"
                    onChange={(e) =>
                      updateGameField(game.sortOrder, "awayTeam", e.target.value)
                    }
                  />
                  <TeamMatchSelect
                    rawName={game.awayTeam}
                    side="away"
                    sortOrder={game.sortOrder}
                    match={matchByName.get(game.awayTeam)}
                    teamOptions={teamOptions}
                    choice={choices.get(game.awayTeam)}
                    onChange={setChoiceForName}
                  />
                  {game.needsReview && (
                    <span className="ingest-flag">needs review</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {saveError && (
        // Shown rather than swallowed. A save that fails quietly puts the user back where this
        // feature started: edits on screen that will not be in the tournament, with nothing saying so.
        <div className="review-error" role="alert" data-testid="review-save-error">
          {saveError}
        </div>
      )}

      {confirmError && (
        <div className="review-error" role="alert" data-testid="review-confirm-error">
          {confirmError}
        </div>
      )}

      <div className="review-actions">
        <button
          type="button"
          className="ingest-btn ingest-btn--primary"
          onClick={handleConfirm}
          disabled={confirming}
          data-testid="review-confirm-button"
        >
          {isRebuild
            ? confirming
              ? "Rebuilding schedule..."
              : "Rebuild schedule"
            : confirming
              ? "Creating tournament..."
              : "Create tournament"}
        </button>
      </div>
    </div>
  );
}

interface MergeSummaryPanelProps {
  result: ScheduleMergeResultResponse;
  /** Called after a conflict is resolved so it can be dropped from the list. */
  onResolved: (gameId: string) => void;
  /**
   * Called with the panel's own result when the owner is done reviewing (refreshes
   * the calendar + closes). Passing the prop back avoids a stale closure over the
   * parent's merge-result state.
   */
  onDone: (result: ScheduleMergeResultResponse) => void;
  /** The component-wide abort controller so unmount aborts an in-flight resolve. */
  signalRef: RefObject<AbortController | null>;
}

/**
 * The owner's "what changed" summary after a re-import merged into an existing
 * tournament. Shows the change counts, a prominent warning when removals were held
 * by the mass-delete guard, a list of conflicts to resolve (keep mine / take
 * theirs), and a "N new teams to review" callout. Purely a display of the merge
 * result the server already applied; the owner dismisses it with Done.
 */
function MergeSummaryPanel({
  result,
  onResolved,
  onDone,
  signalRef,
}: MergeSummaryPanelProps) {
  const summary = mergeSummary(result);
  // A check reads "all good"; when there is something to act on (held removals or
  // unresolved conflicts) show a neutral note icon instead so it doesn't mislead.
  const needsAttention = summary.hasHeldMassDelete || summary.hasConflicts;

  return (
    <div className="merge-summary" data-testid="merge-summary">
      <div className="merge-summary__head">
        <span className="merge-summary__icon" aria-hidden="true">
          {needsAttention ? "📋" : "✅"}
        </span>
        <p className="merge-summary__headline" data-testid="merge-summary-headline">
          {summary.headline}
        </p>
      </div>

      {summary.hasHeldMassDelete && (
        <div
          className="merge-summary__warning"
          role="alert"
          data-testid="merge-summary-held-mass-delete"
        >
          <span className="merge-summary__warning-icon" aria-hidden="true">
            ⚠️
          </span>
          {summary.heldMassDeleteMessage}
        </div>
      )}

      {summary.hasNewTeams && (
        <p className="merge-summary__new-teams" data-testid="merge-summary-new-teams">
          {summary.newTeamsLabel}
          {result.newTeamNames.length > 0 && (
            <span className="merge-summary__new-teams-names">
              {" "}
              ({result.newTeamNames.join(", ")})
            </span>
          )}
        </p>
      )}

      {summary.hasConflicts && (
        <div className="merge-summary__conflicts" data-testid="merge-summary-conflicts">
          <h3 className="merge-summary__conflicts-title">
            Games that need your review
          </h3>
          <p className="merge-summary__conflicts-lead">
            You changed these games, and the new import wanted to change them too.
            Keep your version or take the imported one.
          </p>
          <ul className="merge-summary__conflict-list">
            {result.conflicts.map((game) => (
              <ConflictRow
                key={game.gameId}
                calendarEventId={result.calendarEventId}
                game={game}
                onResolved={onResolved}
                signalRef={signalRef}
              />
            ))}
          </ul>
        </div>
      )}

      <div className="review-actions">
        <button
          type="button"
          className="ingest-btn ingest-btn--primary"
          onClick={() => onDone(result)}
          data-testid="merge-summary-done"
        >
          {summary.hasConflicts ? "Review later" : "Done"}
        </button>
      </div>
    </div>
  );
}

interface ConflictRowProps {
  calendarEventId: string;
  game: ScheduleMergeGameResponse;
  onResolved: (gameId: string) => void;
  /** The component-wide abort controller so unmount aborts an in-flight resolve. */
  signalRef: RefObject<AbortController | null>;
}

const RESOLVE_FALLBACK =
  "We could not save that choice. Please try again.";

/**
 * One flagged conflict with keep-mine / take-theirs actions. Calls the
 * resolve-conflict endpoint and, on success, asks the parent to drop it from the
 * list. Each row owns its own in-flight and error state so one row's failure never
 * blocks the others. Post-await state is guarded on the shared abort signal so a
 * resolve completing after unmount never sets state or calls back.
 */
function ConflictRow({
  calendarEventId,
  game,
  onResolved,
  signalRef,
}: ConflictRowProps) {
  const [resolving, setResolving] = useState<"keepMine" | "takeTheirs" | null>(
    null
  );
  const [error, setError] = useState("");

  const resolve = async (action: "keepMine" | "takeTheirs") => {
    const signal = signalRef.current?.signal;
    setResolving(action);
    setError("");
    try {
      await resolveGameConflict(calendarEventId, game.gameId, action, signal);
      if (signal?.aborted) return;
      onResolved(game.gameId);
    } catch (err) {
      if (signal?.aborted) return;
      setError(getSafeErrorMessage(err, RESOLVE_FALLBACK));
      setResolving(null);
    }
  };

  return (
    <li
      className="merge-conflict"
      data-testid={`merge-conflict-${game.gameId}`}
    >
      <div className="merge-conflict__info">
        <span className="merge-conflict__label">{game.label}</span>
      </div>
      <div className="merge-conflict__actions">
        <button
          type="button"
          className="ingest-btn"
          disabled={resolving !== null}
          onClick={() => resolve("keepMine")}
          data-testid={`merge-conflict-keep-mine-${game.gameId}`}
        >
          {resolving === "keepMine" ? "Saving..." : "Keep mine"}
        </button>
        <button
          type="button"
          className="ingest-btn ingest-btn--primary"
          disabled={resolving !== null}
          onClick={() => resolve("takeTheirs")}
          data-testid={`merge-conflict-take-theirs-${game.gameId}`}
        >
          {resolving === "takeTheirs" ? "Saving..." : "Take theirs"}
        </button>
      </div>
      {error && (
        <p
          className="merge-conflict__error"
          role="alert"
          data-testid={`merge-conflict-error-${game.gameId}`}
        >
          {error}
        </p>
      )}
    </li>
  );
}

/**
 * The timezone adornment under a game's editable time input. Shows the field
 * timezone's short label (e.g. "EDT") and, when the tz was assumed rather than
 * resolved from the field, a subtle plain-language hint.
 */
function GameTimeZone({ game }: { game: ExtractedGame }) {
  const label = gameTimeLabel(game);
  // Nothing to show when there's no time text (the input's placeholder covers it).
  if (!label) return null;

  return (
    <div className="review-tz" data-testid={`review-tz-${game.sortOrder}`}>
      <span className="review-tz__label">{label}</span>
      {game.timezoneAssumed && (
        <span
          className="review-tz__assumed"
          title="We could not confirm this field's location, so we assumed a timezone."
        >
          timezone assumed
        </span>
      )}
    </div>
  );
}

interface TeamMatchSelectProps {
  rawName: string;
  side: "home" | "away";
  sortOrder: number;
  match: TeamMatch | undefined;
  teamOptions: { teamId: string; name: string }[];
  choice: TeamChoice | undefined;
  onChange: (rawName: string, choice: TeamChoice) => void;
}

/**
 * The Home/Away team picker for a single game side. Blank sides (a messy
 * extraction) have nothing to resolve; everything else delegates to the
 * searchable combobox.
 */
function TeamMatchSelect(props: TeamMatchSelectProps) {
  if (props.rawName.trim().length === 0) {
    return <span className="review-empty-side">-</span>;
  }
  return <TeamMatchCombobox {...props} />;
}

/** One flat, keyboard-navigable option in the team combobox's list. */
type ComboOption =
  | { key: string; kind: "create"; label: string }
  | { key: string; kind: "unmatched"; label: string }
  | {
      key: string;
      kind: "existing";
      teamId: string;
      label: string;
      suggested: boolean;
    };

/**
 * A searchable team picker replacing the native <select>: type to filter the
 * org's teams by name, with the server's suggested candidates surfaced first and
 * "Create new team" / "Leave unmatched" as fixed actions. Picking round-trips to
 * a TeamChoice. Keyboard: ↑/↓ to move, Enter to pick, Esc to close; an outside
 * click or blur closes. The default choice (create) is shown as the trigger label
 * until the user changes it.
 */
function TeamMatchCombobox({
  rawName,
  side,
  sortOrder,
  match,
  teamOptions,
  choice,
  onChange,
}: TeamMatchSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();

  const candidates = useMemo(() => match?.candidates ?? [], [match]);

  const teamNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of teamOptions) map.set(t.teamId, t.name);
    for (const c of candidates) map.set(c.teamId, c.name);
    return map;
  }, [teamOptions, candidates]);

  const current = choice ?? { kind: "unmatched" };
  const triggerLabel =
    current.kind === "existing" && current.teamId
      ? teamNameById.get(current.teamId) ?? "Unknown team"
      : current.kind === "create"
        ? `Create "${rawName}"`
        : "Leave unmatched";

  // Options in display order: create, suggested candidates, all other teams,
  // unmatched. Teams (not the fixed actions) are filtered by the search query.
  const options = useMemo<ComboOption[]>(() => {
    const q = query.trim().toLowerCase();
    const hit = (name: string) => q.length === 0 || name.toLowerCase().includes(q);
    const candidateIds = new Set(candidates.map((c) => c.teamId));

    const list: ComboOption[] = [
      { key: "__create__", kind: "create", label: `Create new team "${rawName}"` },
    ];
    for (const c of candidates) {
      if (hit(c.name)) {
        list.push({
          key: `s:${c.teamId}`,
          kind: "existing",
          teamId: c.teamId,
          label: c.name,
          suggested: true,
        });
      }
    }
    for (const t of teamOptions) {
      if (!candidateIds.has(t.teamId) && hit(t.name)) {
        list.push({
          key: `o:${t.teamId}`,
          kind: "existing",
          teamId: t.teamId,
          label: t.name,
          suggested: false,
        });
      }
    }
    list.push({ key: "__unmatched__", kind: "unmatched", label: "Leave unmatched" });
    return list;
  }, [query, candidates, teamOptions, rawName]);

  // Focus the search box when the panel opens; reset the query on close.
  useEffect(() => {
    if (open) {
      setActive(0);
      inputRef.current?.focus();
    } else {
      setQuery("");
    }
  }, [open]);

  // Close on an outside click so a stray dropdown doesn't linger.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const choose = (opt: ComboOption) => {
    if (opt.kind === "create") onChange(rawName, { kind: "create" });
    else if (opt.kind === "unmatched") onChange(rawName, { kind: "unmatched" });
    else onChange(rawName, { kind: "existing", teamId: opt.teamId });
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, options.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = options[active];
      if (opt) choose(opt);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  const isSelected = (opt: ComboOption) =>
    (opt.kind === "create" && current.kind === "create") ||
    (opt.kind === "unmatched" && current.kind === "unmatched") ||
    (opt.kind === "existing" &&
      current.kind === "existing" &&
      current.teamId === opt.teamId);

  return (
    <div className="review-team review-combo" ref={rootRef}>
      <button
        type="button"
        className={`review-combo__trigger${
          current.kind === "unmatched" ? " review-combo__trigger--muted" : ""
        }`}
        data-testid={`review-${side}-select-${sortOrder}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`${side === "home" ? "Home" : "Away"} team for "${rawName}"`}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="review-combo__value">{triggerLabel}</span>
        <span className="review-combo__caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div className="review-combo__panel">
          <input
            ref={inputRef}
            className="review-combo__search"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={
              options[active] ? `${listId}-opt-${active}` : undefined
            }
            aria-autocomplete="list"
            placeholder="Search teams..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
          />
          <ul className="review-combo__list" id={listId} role="listbox">
            {options.map((opt, i) => (
              <li
                key={opt.key}
                id={`${listId}-opt-${i}`}
                role="option"
                aria-selected={isSelected(opt)}
                className={`review-combo__option${
                  i === active ? " review-combo__option--active" : ""
                }${opt.kind !== "existing" ? " review-combo__option--meta" : ""}`}
                onMouseEnter={() => setActive(i)}
                // mousedown (not click) so the pick registers before the input blurs.
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(opt);
                }}
              >
                <span className="review-combo__option-label">{opt.label}</span>
                {opt.kind === "existing" && opt.suggested && (
                  <span className="review-combo__badge">Suggested</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* The raw extracted name sits BELOW the picker (like the time zone hint under a time) so every
          row's input control lines up along the top. */}
      <span className="review-team__raw" title={rawName}>
        {rawName}
      </span>
      {match?.isAmbiguous && current.kind === "create" && (
        <span className="review-team__ambiguous">
          Several teams match — creating new unless you pick one
        </span>
      )}
    </div>
  );
}
